<script setup lang="ts">
// A text input with suggestion chips under it (Sierra's .sugg), instead of <datalist>,
// which iPad AutoFill breaks (Sierra line 588). A tap on a suggestion fills the field.
// While something is typed, only suggestions containing it show; at most `limit`.
// Other attributes go to the <input>.
import { computed, useId } from 'vue';

defineOptions({ inheritAttrs: false });
const props = withDefaults(defineProps<{ modelValue: string; label: string; suggestions: readonly string[]; placeholder?: string; limit?: number }>(), {
  placeholder: '',
  limit: 8,
});
const emit = defineEmits<{ 'update:modelValue': [value: string] }>();
const id = useId();

const shown = computed(() => {
  const needle = props.modelValue.trim().toLowerCase();
  return props.suggestions.filter((entry) => entry !== props.modelValue && (needle === '' || entry.toLowerCase().includes(needle))).slice(0, props.limit);
});
</script>

<template>
  <div class="itc-field itc-suggest">
    <label class="itc-field-label" :for="`${id}-input`">{{ label }}</label>
    <input
      v-bind="$attrs"
      :id="`${id}-input`"
      class="itc-input"
      autocomplete="off"
      :value="modelValue"
      :placeholder="placeholder"
      @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    />
    <div v-if="shown.length > 0" class="itc-suggest__chips" role="group" :aria-label="`Suggestions for ${label}`">
      <button v-for="entry in shown" :key="entry" type="button" class="itc-suggest__chip" @click="emit('update:modelValue', entry)">{{ entry }}</button>
    </div>
  </div>
</template>

<style scoped>
.itc-suggest__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 1px;
}
.itc-suggest__chip {
  min-height: var(--itc-tap-small);
  padding: 0 12px;
  border: 1px solid var(--itc-line-2);
  border-radius: var(--itc-r-pill);
  background: var(--itc-panel-2);
  color: var(--itc-fg);
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}
.itc-suggest__chip:active {
  border-color: var(--itc-accent);
  background: var(--itc-accent-soft);
}
</style>
